import React, { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const ROLE_PT = { SUPERADMIN: "Superadmin", DIRECAO: "Direção", COORDENACAO: "Coordenação", PROFESSOR: "Professor", COZINHEIRA: "Cozinheira" };

function Ranking({ title, rows, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <Card className="p-4" data-testid={testid}>
      <h3 className="mb-3 text-sm font-bold text-slate-700">{title}</h3>
      {rows.length === 0 && <p className="text-xs text-slate-400">Sem dados no período.</p>}
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.key} className="text-xs">
            <div className="flex justify-between gap-2"><span className="truncate text-slate-600">{r.key}</span><span className="font-bold text-slate-800">{r.n}</span></div>
            <div className="mt-1 h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-blue-600" style={{ width: `${(r.n / max) * 100}%` }} /></div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function AssistantStats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/assistant/admin/stats", { params: { days: 30 } }).then(({ data }) => setS(data)); }, []);
  if (!s) return <p className="text-sm text-slate-400">Carregando...</p>;
  const cards = [["Perguntas (30 dias)", s.total, "stat-total"], ["Ajuda da tela", s.screen_help, "stat-screen"], ["Sem resposta na base", s.no_doc, "stat-nodoc"]];
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        {cards.map(([l, v, t]) => (
          <Card key={t} className="p-4"><div className="text-xs text-slate-500">{l}</div><div className="mt-1 text-2xl font-bold text-slate-900" data-testid={t}>{v}</div></Card>
        ))}
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <Ranking title="Por perfil" rows={s.by_role} testid="stat-by-role" />
        <Ranking title="Por tela" rows={s.by_route} testid="stat-by-route" />
        <Ranking title="Tutoriais mais consultados" rows={s.by_article} testid="stat-by-article" />
      </div>
      <p className="text-xs text-slate-400">As estatísticas são agregadas: não guardam nomes, conversas nem dados pessoais.</p>
    </div>
  );
}

export function UnansweredList() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/assistant/admin/unanswered").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const resolve = async (id) => { await api.delete(`/assistant/admin/unanswered/${id}`); toast.success("Marcada como resolvida"); load(); };
  if (!rows.length) return <Card className="p-6 text-sm text-slate-500" data-testid="unanswered-empty">Nenhuma pergunta sem resposta até agora.</Card>;
  return (
    <Card className="divide-y divide-slate-100" data-testid="unanswered-list">
      {rows.map((r) => (
        <div key={r.id} className="flex items-start gap-3 p-4" data-testid={`unanswered-${r.id}`}>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-800">{r.text}</p>
            <p className="mt-1 text-xs text-slate-400">{r.count}× · {(r.roles || []).map((x) => ROLE_PT[x] || x).join(", ")} · tela {r.route} · última em {r.last_at?.slice(0, 10).split("-").reverse().join("/")}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => resolve(r.id)} data-testid={`unanswered-resolve-${r.id}`}><Trash2 className="mr-1 h-3.5 w-3.5" />Resolvida</Button>
        </div>
      ))}
    </Card>
  );
}
